import toposort from 'toposort';
import { z } from 'zod';

import { ClosedWorkflowStatus, StepDefinitionId, WorkflowDefinitionId, zodBrandedString } from '@willow/types-iso';

import { Expression, ExpressionEvaluator } from '../Expressions/ExpressionEvaluator';
import { ApiStepDefinition, StepDefinition, StepDefinitionExpressionKeys } from './steps/StepDefinition';
import { AutomaticResolutionDefinition, RESERVED_CLOSURE_REASON_IDS } from './traits/AutomaticResolutionDefinition';

/**
 * Base metadata about a workflow definition (returned from list methods)
 */
export type WorkflowDefinitionMetadata = z.infer<typeof WorkflowDefinitionMetadata>;
export const WorkflowDefinitionMetadata = z.object({
  id: zodBrandedString<WorkflowDefinitionId>().meta({
    description: 'The ID of the workflow definition. (Note: this may be either a human-readable ID or a UUID.)',
    example: 'c0ef58c7-4f5c-4352-a775-2a75ea4c6d88',
    type: 'string',
  }),
  version: z.int().nonnegative().meta({
    description: 'The version of the workflow definition',
    example: 1,
  }),
  title: z.string().meta({
    description: 'The title of the workflow definition',
    example: 'Chapter 13 Bankruptcy',
  }),
  description: z.string().meta({
    description: 'The description of the workflow definition',
    example: 'A workflow for Chapter 13 bankruptcy',
  }),
  tags: z
    .array(z.string())
    .optional()
    .meta({
      description: 'The tags of the workflow definition',
      example: ['bankruptcy', 'chapter-13'],
    }),
  automaticallyComplete: AutomaticResolutionDefinition,
  automaticallyCancel: AutomaticResolutionDefinition,
  canManuallyComplete: Expression('boolean').meta({
    description: 'Whether the workflow definition can be manually completed.',
    example: ExpressionEvaluator.boolean`true`,
  }),
  canManuallyCancel: Expression('boolean').meta({
    description: 'Whether the workflow definition can be manually canceled.',
    example: ExpressionEvaluator.boolean`true`,
  }),
  manualClosureReasons: z
    .array(
      z.object({
        label: z.string(),
        value: ClosedWorkflowStatus.safeExtend({ reasonId: z.string() }),
      }),
    )
    .optional()
    .meta({
      description: 'Reasons for manual workflow closure.',
      example: [
        { label: 'Relief granted', value: { status: 'completed', reasonId: 'reliefGranted' } },
        {
          label: 'No longer applicable',
          value: { status: 'cancelled', reasonId: 'noLongerApplicable' },
        },
      ],
    }),
});

export const ApiWorkflowDefinitionMetadata = WorkflowDefinitionMetadata.pick({
  id: true,
  version: true,
  title: true,
  description: true,
});

/**
 * Full workflow definition minus step-specific definitions (referenced internally by WorkflowInstance)
 */
export type WorkflowDefinitionSansSteps = z.infer<typeof WorkflowDefinitionSansSteps>;
export const WorkflowDefinitionSansSteps = WorkflowDefinitionMetadata.extend({
  // TODO things that aren't on steps but also shouldn't be returned by list methods
  // computedFields: z.array({ ...something ... }),
  // isComplete: Condition,
});

const ApiWorkflowDefinitionMask = {
  id: true,
  version: true,
  title: true,
  description: true,
} as const satisfies Partial<Record<keyof WorkflowDefinitionSansSteps, true>>;

export const ApiWorkflowDefinition = WorkflowDefinitionSansSteps.pick(ApiWorkflowDefinitionMask).extend({
  steps: z.array(ApiStepDefinition),
});

/**
 * Full workflow definition with step-specific definitions (as stored in DB, returned by get methods)
 */
export type WorkflowDefinition = z.infer<typeof WorkflowDefinition>;
export const WorkflowDefinition = WorkflowDefinitionSansSteps.extend({
  steps: z.array(StepDefinition),
}).superRefine((wfd, ctx) => {
  validateFieldNamesAreUnique(wfd, ctx);
  validateStepIdsAreUnique(wfd, ctx);
  validateActivationPathIsNonCyclical(wfd, ctx);
  validateCanBeCompleted(wfd, ctx);
  validateClosureReasonIdsAreUnique(wfd, ctx);
  validateClosureReasonsDoNotReferenceReservedReasonIds(wfd, ctx);
});

function validateClosureReasonIdsAreUnique(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const allReasonIds = new Set<string>();
  wfd.manualClosureReasons?.forEach((reason, idx) => {
    if (allReasonIds.has(reason.value.reasonId)) {
      ctx.addIssue({
        code: 'custom',
        message: 'Each manual closure reason must have a unique reasonId',
        path: ['manualClosureReasons', idx, 'value', 'reasonId'],
      });
    }
    allReasonIds.add(reason.value.reasonId);
  });
}

function validateClosureReasonsDoNotReferenceReservedReasonIds(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const reservedReasonIds = new Set<string>(Object.values(RESERVED_CLOSURE_REASON_IDS));
  wfd.manualClosureReasons?.forEach((reason, idx) => {
    if (reservedReasonIds.has(reason.value.reasonId)) {
      ctx.addIssue({
        code: 'custom',
        message: `Manual closure reason ${reason.value.reasonId} references a reserved reason ID`,
        path: ['manualClosureReasons', idx, 'value', 'reasonId'],
      });
    }
  });
}

function validateStepIdsAreUnique(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const allStepDefinitionIds = new Set<StepDefinitionId>(wfd.steps.map((step) => step.id));
  wfd.steps.forEach((step, i) => {
    for (const expressionKey of StepDefinitionExpressionKeys) {
      if (step[expressionKey] == null) {
        continue;
      }
      const referencedIds = ExpressionEvaluator.getReferencedStepDefinitionIds(step.id, step[expressionKey]);
      for (const referencedId of referencedIds) {
        if (!allStepDefinitionIds.has(referencedId)) {
          ctx.addIssue({
            code: 'custom',
            message: `Step ${step.id} references undefined step ${referencedId}`,
            path: ['steps', i, expressionKey],
          });
        }
      }
    }
  });
}

function validateFieldNamesAreUnique(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const allFieldNames = new Set<string>();
  wfd.steps.forEach((step, i) => {
    const form = step.dataCollection?.form;
    if (form == null) {
      return;
    }

    // validating top-level keys is enough because subkeys will be namespaced by
    // their parents in reports & similar, so conflicts are... fine?
    for (const { key: fieldName } of form) {
      if (allFieldNames.has(fieldName)) {
        ctx.addIssue({
          code: 'custom',
          message: `Field ${fieldName} is not unique`,
          path: ['steps', i, 'dataCollection', 'form', 'fields', fieldName],
        });
      }
      allFieldNames.add(fieldName);
    }
  });
}

/**
 * validate that activation paths (as defined by the step's
 * automaticallyActivate and canManuallyActivate fields are non-cyclical and
 * can be toposorted)
 *
 * implicitly we also consider steps that start without any step dependencies
 * (using a stand-in value for the implied start node in the graph)
 */
function validateActivationPathIsNonCyclical(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const activationDependencies = wfd.steps.flatMap((step) => {
    const START_NODE = '__start__';

    const deps: [StepDefinitionId | typeof START_NODE, StepDefinitionId][] = [];
    if (ExpressionEvaluator.expressionCanBeTrueWithoutStepDependency(step.automaticallyActivate)) {
      deps.push([START_NODE, step.id]);
    }
    deps.push(
      ...ExpressionEvaluator.getReferencedStepDefinitionIds(step.id, step.automaticallyActivate).map(
        (id): [StepDefinitionId, StepDefinitionId] => [id, step.id],
      ),
    );

    if (ExpressionEvaluator.expressionCanBeTrueWithoutStepDependency(step.canManuallyActivate)) {
      deps.push([START_NODE, step.id]);
    }
    deps.push(
      ...ExpressionEvaluator.getReferencedStepDefinitionIds(step.id, step.canManuallyActivate).map(
        (id): [StepDefinitionId, StepDefinitionId] => [id, step.id],
      ),
    );
    return deps;
  });

  try {
    toposort(activationDependencies);
  } catch (e) {
    const msgRegexp = /^Cyclic dependency, node was:(.+)$/;
    if (e instanceof Error && msgRegexp.test(e.message)) {
      const badStepId = JSON.parse(msgRegexp.exec(e.message)?.[1]!);
      const badStepIndex = wfd.steps.findIndex((step) => step.id === badStepId);
      ctx.addIssue({
        code: 'custom',
        message: `cyclical activation path via: ${badStepId}`,
        path: ['steps', badStepIndex],
      });
    }
  }
}

function validateCanBeCompleted(wfd: WorkflowDefinition, ctx: z.RefinementCtx) {
  const cannotAutomaticallyComplete = Array.isArray(wfd.automaticallyComplete)
    ? wfd.automaticallyComplete.every((expr) => ExpressionEvaluator.expressionIsAlwaysFalse(expr.expression))
    : ExpressionEvaluator.expressionIsAlwaysFalse(wfd.automaticallyComplete.expression);
  const cannotManuallyComplete = ExpressionEvaluator.expressionIsAlwaysFalse(wfd.canManuallyComplete);
  if (cannotAutomaticallyComplete && cannotManuallyComplete) {
    ctx.addIssue({
      code: 'custom',
      message:
        'Workflow can never be completed (neither automaticallyComplete nor canManuallyComplete can ever be true)',
      path: ['automaticallyComplete'],
    });
  }
}
